/**
 * LocalStorage Manager
 * Handles localStorage operations with corruption detection and recovery
 */

import { TOKEN_KEYS, tokenStorage } from "../config/api";

/**
 * Keys that should be preserved during logout/cleanup
 */
const PRESERVED_KEYS = [
	"theme",
	"language",
	"color-scheme",
	"preferred-language",
];

/**
 * Validate if a localStorage value is properly formatted JSON or valid string
 */
function isValidStorageValue(key: string, value: string | null): boolean {
	if (!value) return true; // null/empty is valid (means not set)

	// For token keys, should be non-empty strings
	const tokenKeyValues: string[] = Object.values(TOKEN_KEYS);
	if (tokenKeyValues.includes(key)) {
		return typeof value === "string" && value.length > 0 && value !== "undefined" && value !== "null";
	}

	// For other keys, try to parse if it looks like JSON
	if (value.startsWith("{") || value.startsWith("[")) {
		try {
			JSON.parse(value);
			return true;
		} catch {
			return false;
		}
	}

	// Otherwise, it's a valid string value
	return true;
}

/**
 * Detect corrupted localStorage entries
 */
export function detectCorruptedStorage(): string[] {
	const corruptedKeys: string[] = [];

	try {
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key) continue;

			const value = localStorage.getItem(key);
			if (!isValidStorageValue(key, value)) {
				corruptedKeys.push(key);
			}
		}
	} catch (error) {
		console.error("Error detecting corrupted storage:", error);
	}

	return corruptedKeys;
}

/**
 * Clear all user-related data from localStorage
 * Preserves theme, language, and other non-sensitive settings
 */
export function clearUserData(): void {
	try {
		const keysToRemove: string[] = [];

		// Collect keys to remove (everything except preserved keys)
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (key && !PRESERVED_KEYS.some(preserved => key.includes(preserved))) {
				keysToRemove.push(key);
			}
		}

		// Remove collected keys
		keysToRemove.forEach(key => {
			try {
				localStorage.removeItem(key);
			} catch (error) {
				console.error(`Failed to remove key ${key}:`, error);
			}
		});

		console.log(`Cleared ${keysToRemove.length} localStorage items`);
	} catch (error) {
		console.error("Error clearing user data:", error);
	}
}

/**
 * Clear corrupted localStorage entries
 */
export function clearCorruptedStorage(): void {
	const corruptedKeys = detectCorruptedStorage();

	if (corruptedKeys.length > 0) {
		console.warn(`Found ${corruptedKeys.length} corrupted localStorage entries:`, corruptedKeys);

		corruptedKeys.forEach(key => {
			try {
				localStorage.removeItem(key);
				console.log(`Removed corrupted key: ${key}`);
			} catch (error) {
				console.error(`Failed to remove corrupted key ${key}:`, error);
			}
		});
	}
}

/**
 * Validate and repair localStorage on app initialization
 * Returns true if corruption was detected and cleaned
 */
export function validateAndRepairStorage(): boolean {
	try {
		const corruptedKeys = detectCorruptedStorage();

		if (corruptedKeys.length > 0) {
			console.warn("Corrupted localStorage detected. Cleaning up...");
			clearCorruptedStorage();

			// If tokens were corrupted, clear all user data for safety
			const tokenKeys: string[] = Object.values(TOKEN_KEYS);
			const tokensCorrupted = corruptedKeys.some(key => tokenKeys.includes(key));

			if (tokensCorrupted) {
				console.warn("Auth tokens were corrupted. Clearing all user data...");
				clearUserData();
				return true;
			}

			return true;
		}

		return false;
	} catch (error) {
		console.error("Error validating localStorage:", error);
		// If we can't even validate, clear everything for safety
		try {
			clearUserData();
		} catch (clearError) {
			console.error("Failed to clear user data:", clearError);
		}
		return true;
	}
}

/**
 * Complete logout cleanup
 * Clears all tokens and user data
 */
export function performLogoutCleanup(): void {
	try {
		// Clear tokens first
		tokenStorage.clearTokens();

		// Clear all user data
		clearUserData();

		console.log("Logout cleanup completed successfully");
	} catch (error) {
		console.error("Error during logout cleanup:", error);
		// If normal cleanup fails, try nuclear option
		try {
			const preserved: Record<string, string> = {};

			// Save preserved items
			PRESERVED_KEYS.forEach(key => {
				const value = localStorage.getItem(key);
				if (value) preserved[key] = value;
			});

			// Clear everything
			localStorage.clear();

			// Restore preserved items
			Object.entries(preserved).forEach(([key, value]) => {
				localStorage.setItem(key, value);
			});

			console.log("Fallback cleanup completed");
		} catch (fallbackError) {
			console.error("Fallback cleanup also failed:", fallbackError);
		}
	}
}

/**
 * Check if user is authenticated based on token presence and validity
 */
export function isAuthenticated(): boolean {
	try {
		const accessToken = tokenStorage.getAccessToken();
		return !!(accessToken && accessToken !== "undefined" && accessToken !== "null" && accessToken.length > 0);
	} catch {
		return false;
	}
}
